// =============================================================================
// Module Project - Repository: Watchers
// Manage task watchers (users following task updates)
// =============================================================================

import { supabase } from '@/integrations/supabase/client';

export interface TaskWatcher {
  orgMemberId: string;
  userId: string;
  fullName: string | null;
  email: string;
  avatarUrl: string | null;
  createdAt: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// FETCH WATCHERS FOR A TASK
// ─────────────────────────────────────────────────────────────────────────────
export async function fetchTaskWatchers(taskId: string): Promise<TaskWatcher[]> {
  const { data, error } = await supabase
    .from('project_task_watchers')
    .select(`
      org_member_id,
      created_at,
      member:core_organization_members!project_task_watchers_org_member_id_fkey(
        user_id,
        profile:core_profiles!inner(full_name, email, avatar_url)
      )
    `)
    .eq('task_id', taskId);

  if (error) {
    console.error('Error fetching watchers:', error);
    return [];
  }

  return (data || []).map((w: any) => ({
    orgMemberId: w.org_member_id,
    userId: w.member?.user_id || '',
    fullName: w.member?.profile?.full_name || null,
    email: w.member?.profile?.email || '',
    avatarUrl: w.member?.profile?.avatar_url || null,
    createdAt: w.created_at,
  }));
}

// ─────────────────────────────────────────────────────────────────────────────
// ADD WATCHER
// ─────────────────────────────────────────────────────────────────────────────
export async function addWatcher(
  orgId: string,
  taskId: string,
  orgMemberId: string
): Promise<boolean> {
  const { error } = await supabase
    .from('project_task_watchers')
    .insert({
      org_id: orgId,
      task_id: taskId,
      org_member_id: orgMemberId,
    });

  if (error) {
    // Ignore duplicate key errors (already watching)
    if (error.code === '23505') return true;
    console.error('Error adding watcher:', error);
    return false;
  }

  return true;
}

// ─────────────────────────────────────────────────────────────────────────────
// REMOVE WATCHER
// ─────────────────────────────────────────────────────────────────────────────
export async function removeWatcher(
  taskId: string,
  orgMemberId: string
): Promise<boolean> {
  const { error } = await supabase
    .from('project_task_watchers')
    .delete()
    .eq('task_id', taskId)
    .eq('org_member_id', orgMemberId);

  if (error) {
    console.error('Error removing watcher:', error);
    return false;
  }

  return true;
}

// ─────────────────────────────────────────────────────────────────────────────
// CHECK IF USER IS WATCHING
// ─────────────────────────────────────────────────────────────────────────────
export async function isWatching(
  taskId: string,
  orgMemberId: string
): Promise<boolean> {
  const { data, error } = await supabase
    .from('project_task_watchers')
    .select('org_member_id')
    .eq('task_id', taskId)
    .eq('org_member_id', orgMemberId)
    .maybeSingle();

  if (error) {
    console.error('Error checking watcher:', error);
    return false;
  }

  return data !== null;
}

// ─────────────────────────────────────────────────────────────────────────────
// AUTO-WATCH: Add creator, owner, and assignees as watchers
// ─────────────────────────────────────────────────────────────────────────────
export async function autoWatchTask(
  orgId: string,
  taskId: string,
  memberIds: string[]
): Promise<void> {
  if (memberIds.length === 0) return;

  const watcherRecords = memberIds.map(memberId => ({
    org_id: orgId,
    task_id: taskId,
    org_member_id: memberId,
  }));

  const { error } = await supabase
    .from('project_task_watchers')
    .upsert(watcherRecords, { onConflict: 'task_id,org_member_id', ignoreDuplicates: true });

  if (error) {
    console.error('Error auto-watching task:', error);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// GET WATCHER MEMBER IDS (for notifications)
// ─────────────────────────────────────────────────────────────────────────────
export async function getWatcherMemberIds(taskId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('project_task_watchers')
    .select('org_member_id')
    .eq('task_id', taskId);

  if (error) {
    console.error('Error fetching watcher IDs:', error);
    return [];
  }

  return (data || []).map(w => w.org_member_id);
}
